import { FilterDescriptor, filterBy } from "@progress/kendo-data-query";
import {
  MultiSelect,
  MultiSelectFilterChangeEvent,
  MultiSelectPageChangeEvent
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps } from "@progress/kendo-react-form";
import { Hint, Error } from "@progress/kendo-react-labels";
import { useEffect, useState, useRef } from "react";
import { sortDataFunc } from "../../../utils/dropdownSort";
import { Page } from "../../../types/Page";
import userGroupsService from '../../../services/usergroups.service';
import { Group } from '../../../types/user-groups/Group';

interface CustomMultiSelectGroupsProps extends Omit<FieldRenderProps, ""> {
  onFocus?: () => void;
  onBlur?: () => void;
  validationMessage?: string | null;
  touched?: boolean;
  modified?: boolean;
  visited?: boolean;
  valid?: boolean;
  children?: any;
}

const pageSize = 10;
var allGroups : Group[] = [];

const CustomMultiSelectGroups = (fieldRenderProps: CustomMultiSelectGroupsProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    max,
    rows,
    value,
    showTextLimitHint,
    loading,
    textField,
    sorting = false, // if true, sorting enabled.
    sortField, // if 'data' is array of object then send the field name (on the basis of which you want sorting) in 'sortField' prop as string . if 'data' is array of primitive datatype then no need to send any 'sortField' prop.
    sortOrder = "asc", // send 'asc' or 'desc' for ascending or descending sorting respectively.
    filtering = false, // if true, filtering enabled.
    filterFields, // send those field names in the form of array of strings on the basis of which you want filtering. If no 'filterFields' prop is sent then kendo would take 'textField' prop to do the filtering. If no 'textField' and no 'filterFields' props are there, then no filtering will be done.
    ...others
  } = fieldRenderProps;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const [skip, setSkip] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const maxSkip = useRef<number>(0);
  const [searchVal, setSearchVal] = useState<string | undefined>(undefined);

  const pageChange = (event: MultiSelectPageChangeEvent) => {
      setSkip(event.page.skip);
  };

  const filterChange = (event : MultiSelectFilterChangeEvent) => {
    setSearchVal(event.filter.value);
    allGroups.length = 0;
    maxSkip.current = 0;
    setTotal(0);
    setSkip(0);
  }

  const sortUserGroups = (getGroups: Group[]): Group[] => {
      const sortedGroups: Group[] = getGroups.sort((a: Group, b: Group) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }
        return 0;
      });
      return sortedGroups;
    };

  const fetchGroups = async () => {
    if(skip <= total)
    {
      if(skip >= maxSkip.current )
      {
        maxSkip.current = skip;          

        const allGroupsPaginated = await userGroupsService.fetchAllGroupsPaginated(skip,pageSize, searchVal);
        setTotal(allGroupsPaginated.page.totalRecords??0);

        allGroupsPaginated.data?.forEach(grp => {
          if(allGroups.findIndex((x => x.id === grp.id)) == -1)
            allGroups.push(grp);
        });

        allGroups = sortUserGroups(allGroups);

        setUserGroups(allGroups.slice(skip, skip + pageSize));
      }
      else{
        setUserGroups(allGroups.slice(skip, skip + pageSize));
      }      
    }
  };

  useEffect(()=>{
    fetchGroups();    
  },[skip,searchVal]);


  return (
    <div className="multiselectBox multiselectArrowIcon">
      <div className={"k-form-field-wrap d-flex position-relative"}>
        <MultiSelect
          style={{ zIndex: 1 }}
          onFilterChange={filterChange}
          filterable={filtering}
          loading={loading}
          data={userGroups}
          value={value}
          disabled={disabled}
          textField={textField}
          virtual={{
              total: total,
              skip: skip,
              pageSize: pageSize
            }}
          onChange={(e) => fieldRenderProps.onChange({ value: e.value })}
          onPageChange={pageChange}
          popupSettings={{
                    height: '250px'
          }}
          {...others}
        />
        {showTextLimitHint && (
          <Hint direction={"end"}>
            {value.length} / {max}
          </Hint>
        )}
        {showHint && <Hint id={hindId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </div>
  );
};

export default CustomMultiSelectGroups;
